/* eslint-disable react-hooks/exhaustive-deps */
import produce from 'immer';
import { useEffect, useReducer, useState } from 'react';
import { Helmet } from 'react-helmet';
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input';
import { useDispatch, useSelector } from 'react-redux';

import Logo from '../assets/img/Logo.svg';
import Check from '../assets/svg/sell/tick_check.svg';

import { postSellComment, postSellDomain } from 'store/actions/sell.action';
import * as types from 'store/actions/types';
import { RootState } from 'store/reducers';
import { history } from 'utils/handler';

interface sellForm {
  name: string;
  email: string;
  phone: string;
  domain: string;
  price: string;
  minQuote: string;
  terms: boolean;
  comment: string;
  file: string | File;
}
interface ReducerAction {
  type: string;
  name: string;
  payload?: string | boolean | File | Partial<sellForm>;
}

const initialState = {
  name: '',
  email: '',
  phone: '',
  domain: '',
  price: '',
  minQuote: '',
  comment: '',
  file: '',
  terms: false,
};

const domainReducer = produce((state: sellForm, action: ReducerAction) => {
  const { type, name, payload } = action;
  switch (type) {
    case 'UPDATE':
      state = Object.assign(state, payload);
      return;
    case 'ON_TYPE':
      state[name] = payload;
      return;
    default:
      return;
  }
});

const errorReducer = produce((state: sellForm, action: ReducerAction) => {
  const { type, name, payload } = action;
  switch (type) {
    case 'ADD_ERROR':
      state[name] = payload;
      return;
    case 'DELETE_ERROR':
      state[name] = '';
      return;
    default:
      return;
  }
});

const getSource = () => {
  const sell_ref = localStorage.getItem('sell_ref');
  const sell_referrer = localStorage.getItem('sell_referrer');
  const appraiseDomain = localStorage.getItem('sell_appraise');

  let source = '';
  if (sell_ref) source = sell_ref;
  if (sell_referrer && sell_ref) source = source + ' --> ' + sell_referrer;
  else if (sell_referrer) source = sell_referrer;
  if (appraiseDomain && (sell_ref || sell_referrer)) source = source + ' --> Appraised domain: ' + appraiseDomain;
  else if (appraiseDomain) source = 'Appraised domain: ' + appraiseDomain;
  return source;
};

const removeSource = () => {
  localStorage.removeItem('sell_ref');
  localStorage.removeItem('sell_referrer');
  localStorage.removeItem('sell_appraise');
};
const removeWorth = () => {
  localStorage.removeItem('_sell_value');
  localStorage.removeItem('_sell_worth');
};

function SellForm() {
  const search = history.location.search;
  const query = new URLSearchParams(search);
  const dispatch = useDispatch();
  const [domainDetails, setDomainDetails] = useReducer(domainReducer, initialState);
  const [error, setError] = useReducer(errorReducer, initialState);
  const [showSubmitted, setShowSubmitted] = useState(false);
  const [showComment, setShowComment] = useState(false);
  // const [fileLoad, setFileLoad] = useState(false);
  const [apiLoad, setApiLoad] = useState(false);
  const [showSingle, setShowSingle] = useState(false);
  const [userRoleId, setUserRoleId] = useState('');
  const { localCountryStr } = useSelector((state: RootState) => state.mainReducer);
  const { myProfile } = useSelector((state: RootState) => state.userReducer);

  useEffect(() => {
    const show = query.get('show');
    const domain = query.get('domain');
    const sell_ref = query.get('ref');
    if (sell_ref) localStorage.setItem('sell_ref', sell_ref);
    const sell_referrer = document.referrer;
    if (sell_referrer) localStorage.setItem('sell_referrer', sell_referrer);
    if (show === 'comment') setShowComment(true);
    else if (show === 'thankyou') {
      setShowComment(true);
      setShowSubmitted(true);
    }
    if (domain) {
      setDomainDetails({ type: 'ON_TYPE', name: 'domain', payload: domain });
      localStorage.setItem('sell_appraise', domain);
      setShowSingle(true);
    }
  }, []);

  useEffect(() => {
    if (myProfile?.email) {
      const { name, email, phone } = myProfile;
      setDomainDetails({ type: 'UPDATE', name: '', payload: { name, email, phone } });
    }
  }, [myProfile]);

  const validation = {
    name(value: string) {
      const empty = !value || !value.trim();
      if (empty) setError({ type: 'ADD_ERROR', name: 'name', payload: "Full name can't be empty." });
      else setError({ type: 'DELETE_ERROR', name: 'name' });
      if (empty) return true;
      else return false;
    },
    email(value: string) {
      const empty = !value || !value.trim();
      const isValid = value && !/^([a-zA-Z0-9_.-])+@(([a-zA-Z0-9-])+\.)+([a-zA-Z0-9]{2,4})+$/.test(value);
      if (empty) setError({ type: 'ADD_ERROR', name: 'email', payload: "Email address can't be empty." });
      else if (isValid) setError({ type: 'ADD_ERROR', name: 'email', payload: 'Email address is not valid.' });
      else setError({ type: 'DELETE_ERROR', name: 'email' });
      if (empty || isValid) return true;
      else return false;
    },
    phone(value: string | undefined) {
      const empty = !value || !value.trim();
      const isValid = value && !isValidPhoneNumber(value);
      if (empty) setError({ type: 'ADD_ERROR', name: 'phone', payload: "Phone number can't be empty." });
      else if (isValid) setError({ type: 'ADD_ERROR', name: 'phone', payload: 'Phone number is not valid.' });
      else setError({ type: 'DELETE_ERROR', name: 'phone' });
      if (empty || isValid) return true;
      else return false;
    },
    domain(value: string) {
      const empty = !value || !value.trim();
      const actualDomain = String(value)
        .split(',')
        .filter((x) => x.trim());
      const validDomain = actualDomain.filter((x) => /\w+\.\w\w+/gm.test(x));
      const isValid = value && actualDomain.length !== validDomain.length;
      if (empty) setError({ type: 'ADD_ERROR', name: 'domain', payload: "Domain name can't be empty." });
      else if (isValid) setError({ type: 'ADD_ERROR', name: 'domain', payload: 'Domain name is not valid.' });
      else setError({ type: 'DELETE_ERROR', name: 'domain' });
      if (empty || isValid) return true;
      else return false;
    },
    price(value: string) {
      const empty = !value || !value.trim();
      const less = value && Number(value) < 1;
      if (empty) setError({ type: 'ADD_ERROR', name: 'price', payload: "Domain price can't be empty." });
      else if (less) setError({ type: 'ADD_ERROR', name: 'price', payload: "Domain price can't be negative." });
      else setError({ type: 'DELETE_ERROR', name: 'price' });
      if (empty || less) return true;
      else return false;
    },
    minQuote(value: string) {
      const empty = !value || !value.trim();
      const less = value && Number(value) < 1;
      if (empty) setError({ type: 'ADD_ERROR', name: 'minQuote', payload: "Minimum quote can't be empty." });
      else if (less) setError({ type: 'ADD_ERROR', name: 'minQuote', payload: "Minimum quote can't be negative." });
      else setError({ type: 'DELETE_ERROR', name: 'minQuote' });
      if (empty || less) return true;
      else return false;
    },
    terms(value: boolean) {
      if (!value) setError({ type: 'ADD_ERROR', name: 'terms', payload: 'Please accept privacy policy & terms of service.' });
      else setError({ type: 'DELETE_ERROR', name: 'terms' });
      if (!value) return true;
      else return false;
    },
    file(value: File | string) {
      const name = typeof value !== 'string' && value?.name;
      if (!name) setError({ type: 'ADD_ERROR', name: 'file', payload: 'Please attach the updated csv file.' });
      else setError({ type: 'DELETE_ERROR', name: 'file' });
      if (!name) return true;
      else return false;
    },
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { email, name, phone, terms, domain, minQuote, price } = domainDetails;

    if (!showSingle) {
      let error = false;
      const domainError = validation.domain(domain);
      if (domainError) error = true;
      const emailError = validation.email(email);
      if (emailError) error = true;
      const nameError = validation.name(name);
      if (nameError) error = true;
      const phoneError = validation.phone(phone);
      if (phoneError) error = true;
      const termsError = validation.terms(terms);
      if (termsError) error = true;
      // const fileError = validation.file(file);
      // if (fileError) error = true;

      if (error) return;
      const source = getSource();
      const formName = source.includes('Appraised') ? 'Appraisal Sell' : '';
      // const params = new URLSearchParams({ name, email, phone, source, formName });
      // const query = `?${params.toString()}`;
      setApiLoad(true);
      postSellDomain({ domain, email, name, phone, formName, source })
        .then((data: { userRoleId: string } | boolean) => {
          if (data && typeof data !== 'boolean') {
            setUserRoleId(data.userRoleId);
            setShowComment(true);
            if (source) removeSource();
          }
        })
        .finally(() => setApiLoad(false));
    } else {
      let error = false;
      const domainError = validation.domain(domain);
      if (domainError) error = true;
      const emailError = validation.email(email);
      if (emailError) error = true;
      const nameError = validation.name(name);
      if (nameError) error = true;
      const phoneError = validation.phone(phone);
      if (phoneError) error = true;
      const minQuoteError = validation.minQuote(minQuote);
      if (minQuoteError) error = true;
      const priceError = validation.price(price);
      if (priceError) error = true;
      const termsError = validation.terms(terms);
      if (termsError) error = true;

      if (error) return;
      const source = getSource();
      setApiLoad(true);
      const payload = {};
      const value = localStorage.getItem('_sell_value');
      if (value) payload['value'] = +value;
      const worth = localStorage.getItem('_sell_worth');
      if (worth) payload['worth'] = +worth;
      const formName = source.includes('Appraised') ? 'Appraisal Sell' : '';
      postSellDomain({ ...payload, domain, email, name, phone, minQuote: +minQuote, price: +price, source, formName })
        .then((data: { userRoleId: string } | boolean) => {
          if (data && typeof data !== 'boolean') {
            setUserRoleId(data.userRoleId);
            setShowComment(true);
            if (source) removeSource();
            if (worth || value) removeWorth();
          }
        })
        .finally(() => setApiLoad(false));
    }
  };

  const handleCommentSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const { comment } = domainDetails;
    if (comment && String(comment).trim()) {
      setApiLoad(true);
      postSellComment(userRoleId, { comment })
        .then((data) => {
          if (data) setShowSubmitted(true);
        })
        .finally(() => setApiLoad(false));
    } else setShowSubmitted(true);
  };

  // const handleFile = (e) => {
  //   const file: File = e.target.files?.[0];
  //   validation.file(file);
  //   setDomainDetails({ type: 'ON_TYPE', name: 'file', payload: file });
  // };

  return (
    <div className="sell_bg_2 py-5">
      <Helmet>
        <title>Saw.com Domain Selling Service: Get Top $ For Your Domain</title>
      </Helmet>
      <div className="container">
        <div className="d-flex justify-content-center">
          <img src={Logo} alt="" className="sell_cursor_pointer" width={205} onClick={() => history.push('/sell')} />
        </div>
        {showComment ? (
          <>
            <div className="text-center my-5">
              <div className="sell_h2">Nice One! Your Names Have Been Submitted…</div>
            </div>
            <div className="sell_form_input_container mb-5 text-center sell_mx_auto">
              <div className="sell_submit_msg_container">
                <div className="sell_h5_v2 mt-4">Our brokers are currently reviewing and adding your names to our system. To complete the onboarding process, please change your nameservers to:</div>
                <div className="mt-4 sell_h3_v1 sell_c2 sell_wt_700">
                  ns1.eftydns.com <br />
                  ns2.eftydns.com
                </div>
                <div className="mt-4 sell_h5_v2">
                  We’ll be in touch about your names soon - but you can send any additional information (such as pricing or preferred contact method) to us, our friendly Onboarding Agent. Feel free to message us anytime at{' '}
                  <a className="sell_c" href="mailto:buzz@saw.com">
                    buzz@saw.com
                  </a>
                  .
                </div>
                <div className="mt-4 sell_h5_v2 sell_wt_700">Let’s get started and sell those names!</div>
                {showSubmitted ? (
                  <>
                    <img className="mt-5" src={Check} alt="" />
                    <div className="mt-4 sell_p mb-5">
                      <span className="sell_wt_700">Success!</span> We’ve got your comments and will get back to you ASAP. You’ll get an email from us soon - but don’t forget to update your nameservers in the meantime!
                    </div>
                  </>
                ) : (
                  <>
                    <textarea className="sell_premium_input saw-input mt-5" name="" id="" rows={3} value={domainDetails.comment} onChange={(e) => setDomainDetails({ type: 'ON_TYPE', name: 'comment', payload: e.target.value })} placeholder="Any questions or comments for our team?"></textarea>
                    <div className="d-flex justify-content-center mt-4">
                      <button className="btn btn-grad" onClick={(e) => handleCommentSubmit(e)} disabled={apiLoad}>
                        {apiLoad ? 'Submitting...' : 'Submit'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="text-center my-5">
              <div className=" sell_h2">Start Selling</div>
              <div className="sell_h5_v2 mt-2 sell_form_content sell_mx_auto">
                By pointing your names at our custom landers and getting access to expert <strong>Saw.com</strong> brokers with half a billion dollars in confirmed sales, you give yourself the best chance of selling your domain names! <br /> <br />
                The first stage is submitting your names - so just fill in the form below and one of our expert brokers will contact you soon…
              </div>
            </div>
            <div className="sell_form_input_container mb-5 sell_mx_auto">
              <div className="sell_form_input_section1">
                <div className="sell_form_input_sub_section1">
                  <div className="sell_h5_v2 sell_wt_600">Contact information</div>
                  <div>
                    <input
                      className={`sell_premium_input saw-input sell_width_fix form-control ${error.name ? 'is-invalid' : ''}`}
                      type="text"
                      placeholder="Full Name*"
                      value={domainDetails.name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDomainDetails({ type: 'ON_TYPE', name: 'name', payload: value });
                        validation.name(value);
                      }}
                    />
                    {error.name && <div className="sell_form_error">{error.name}</div>}
                  </div>
                  <div>
                    <input
                      className={`sell_premium_input saw-input sell_width_fix form-control ${error.email ? 'is-invalid' : ''}`}
                      type="text"
                      placeholder="Email Address*"
                      value={domainDetails.email}
                      onChange={(e) => {
                        const value = e.target.value;
                        setDomainDetails({ type: 'ON_TYPE', name: 'email', payload: value });
                        validation.email(value);
                      }}
                    />
                    {error.email && <div className="sell_form_error">{error.email}</div>}
                  </div>
                  <div>
                    <PhoneInput
                      international
                      defaultCountry={localCountryStr}
                      placeholder="Enter phone number"
                      disabled={false}
                      value={domainDetails.phone}
                      onChange={(data) => {
                        setDomainDetails({ type: 'ON_TYPE', name: 'phone', payload: data });
                        validation.phone(data);
                      }}
                      className={`form-control sell_phone_input ${error.phone ? 'is-invalid' : ''}`}
                    />
                    {error.phone && <div className="sell_form_error">{error.phone}</div>}
                  </div>
                </div>
                <div className={`sell_form_input_sub_section1`}>
                  {showSingle ? (
                    <>
                      <div className="sell_h5_v2 sell_wt_600">Paste an individual domain name:</div>
                      <div>
                        <input
                          className={`sell_premium_input saw-input form-control ${error.domain ? 'is-invalid' : ''}`}
                          type="text"
                          placeholder="example.com*"
                          value={domainDetails.domain}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDomainDetails({ type: 'ON_TYPE', name: 'domain', payload: value });
                            validation.domain(value);
                          }}
                        />
                        {error.domain && <div className="sell_form_error">{error.domain}</div>}
                      </div>
                      <div>
                        <input
                          className={`sell_premium_input saw-input form-control ${error.price ? 'is-invalid' : ''}`}
                          type="number"
                          placeholder="Price (in USD)*"
                          value={domainDetails.price}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDomainDetails({ type: 'ON_TYPE', name: 'price', payload: value });
                            validation.price(value);
                          }}
                        />
                        {error.price && <div className="sell_form_error">{error.price}</div>}
                      </div>
                      <div>
                        <input
                          className={`sell_premium_input saw-input form-control ${error.minQuote ? 'is-invalid' : ''}`}
                          type="number"
                          placeholder="Minimum Quote (in USD)*"
                          value={domainDetails.minQuote}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDomainDetails({ type: 'ON_TYPE', name: 'minQuote', payload: value });
                            validation.minQuote(value);
                          }}
                        />
                        {error.minQuote && <div className="sell_form_error">{error.minQuote}</div>}
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="sell_h5_v2 sell_wt_600">Submit your domain names:</div>
                      <div>
                        <textarea
                          className={`sell_premium_input saw-input form-control ${error.domain ? 'is-invalid' : ''}`}
                          rows={7}
                          placeholder="example.com, example.net, example.org"
                          value={domainDetails.domain}
                          onChange={(e) => {
                            const value = e.target.value;
                            setDomainDetails({ type: 'ON_TYPE', name: 'domain', payload: value });
                            validation.domain(value);
                          }}
                        />
                        {error.domain && <div className="sell_form_error">{error.domain}</div>}
                      </div>

                      {/* <div
                        className="sell_form_upload_container sell_mx_auto"
                        onClick={() => {
                          if (fileLoad) return;
                          setFileLoad(true);
                          fetch(baseUrl + '/sell-csv-template')
                            .then((res) => res.blob())
                            .then((res) => {
                              const file = new Blob([res], { type: 'text/csv' });
                              const downloadLink = document.createElement('a');
                              downloadLink.download = 'Selling_domain_list.csv';
                              downloadLink.href = window.URL.createObjectURL(file);
                              downloadLink.style.display = 'none';
                              document.body.appendChild(downloadLink);
                              downloadLink.click();
                              window.URL.revokeObjectURL(downloadLink.href);
                            })
                            .finally(() => setFileLoad(false));
                        }}
                      >
                        <img src={CSV_pic} alt="" height={100} width={100} />
                        <div>Selling_domain_list.csv</div>
                      </div>
                      <div className="sell_h6 sell_wt_500 text-center sell_form_upload_desc">Just download our CSV file, paste your domain names, save and upload using the button below!</div>
                      <div>
                        <div className="d-flex flex-wrap justify-content-center align-items-center">
                          <input type="file" name="" className="d-none" id="upload_domain_csv_file" accept=".csv" onChange={(e) => handleFile(e)} />
                          <label className="sell_btn_secondary sell_h5_v1 mr-2" htmlFor="upload_domain_csv_file">
                            Upload your file
                          </label>
                        </div>
                        {domainDetails.file && typeof domainDetails.file !== 'string' && <div className="sell_h5_v1 text-center">{domainDetails.file.name} file is selected</div>}
                        {error.file && <div className="sell_form_error text-center">{error.file}</div>}
                      </div> */}
                    </>
                  )}
                </div>
              </div>

              <div className="border mt-5" />
              <div className="d-flex justify-content-center mt-4">
                <input
                  type="checkbox"
                  className="flex-shrink-0"
                  id="ultra_premium_domain"
                  checked={domainDetails.terms}
                  onChange={() => {
                    const value = !domainDetails.terms;
                    setDomainDetails({ type: 'ON_TYPE', name: 'terms', payload: value });
                    validation.terms(value);
                  }}
                />
                <label htmlFor="ultra_premium_domain" className="sell_cursor_pointer">
                  I have read and accept the{' '}
                  <span
                    className="sell_c"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch({ type: types.SELL_PRIVACY_POLICY, payload: true });
                    }}
                  >
                    Privacy Policy
                  </span>{' '}
                  &{' '}
                  <span
                    className="sell_c"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      dispatch({ type: types.SELL_TERMS_AND_SERVICE, payload: true });
                    }}
                  >
                    Terms of Service
                  </span>
                </label>
              </div>
              {error.terms && <div className="sell_form_error d-flex justify-content-center">{error.terms}</div>}
              <div className="d-flex justify-content-center mt-4">
                <button className="btn btn-grad" onClick={(e) => handleFormSubmit(e)} disabled={apiLoad}>
                  {apiLoad ? 'Submitting...' : 'Submit'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default SellForm;
